import { useAnnotations } from '../../hooks/useAnnotations';
import { useSeriesMetadata } from '../../hooks/useSeriesMetadata';
import { Tag, getString } from '../../data/dicomTags';
import { AnnotationOverlay, type AnnotationOverlayProps } from './AnnotationOverlay';

type Props = Omit<AnnotationOverlayProps, 'annotations' | 'onCreate' | 'onUpdate' | 'onDelete' | 'frameOfReferenceUID'> & {
  studyInstanceUID: string;
  /** A write the server refused, once it has been rolled back (useAnnotations). */
  onWriteError: (error: Error) => void;
  /** An annotation is being deleted from the slide: the page drops a count of it in progress. */
  onDeleting?: (id: string) => void;
};

/**
 * One slide's annotations over its viewer: the single view's, or one compare
 * pane's. Each reads and writes its own slide's list, so a shape drawn on
 * pane B is filed under B whichever slide the right panel is showing.
 */
export function SlideAnnotations({ studyInstanceUID, onWriteError, onDeleting, ...overlay }: Props) {
  const { annotations, create, update, remove } = useAnnotations(overlay.studyId, overlay.seriesInstanceUID, onWriteError);
  const { data: metadata } = useSeriesMetadata(studyInstanceUID, overlay.seriesInstanceUID);
  return (
    <AnnotationOverlay
      {...overlay}
      frameOfReferenceUID={getString(metadata?.[0], Tag.FrameOfReferenceUID)}
      annotations={annotations}
      // A refusal is reported by the mutation; here it only means there is no
      // saved shape to start naming.
      onCreate={(a) => create.mutateAsync(a).catch(() => undefined)}
      onUpdate={(id, patch) => update.mutate({ id, patch })}
      onDelete={(id) => {
        onDeleting?.(id);
        remove.mutate(id);
      }}
    />
  );
}
